import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { UserPlus, Trash2, Loader2, User, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const ROLES = ["sales", "support", "designer", "production", "painting", "packing", "finance", "operations"];

const AdminTeam = () => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "sales" });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try { const { data } = await api.get("/admin/team"); setMembers(data.members); } catch {}
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        if (!form.name || !form.email || !form.password) return toast.error("Name, email, password required");
        setSaving(true);
        try {
            await api.post("/admin/team", form);
            toast.success(`${form.role} member created`);
            setForm({ name: "", email: "", phone: "", password: "", role: "sales" });
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setSaving(false); }
    };

    const remove = async (m) => {
        if (!confirm(`Remove ${m.name}?`)) return;
        try { await api.delete(`/admin/team/${m.id}`); load(); toast.success("Removed"); } catch { toast.error("Failed"); }
    };

    return (
        <div className="p-8" data-testid="admin-team">
            <h1 className="font-display text-3xl font-bold tracking-tight">Team</h1>
            <p className="text-sm text-muted-foreground">Add sales, support and operations team members with role-based access to the Team Portal.</p>

            <div className="mt-6 rounded-2xl bg-card border p-5" data-testid="add-team-form">
                <h3 className="font-display text-lg font-semibold mb-3">Add a team member</h3>
                <div className="grid md:grid-cols-6 gap-2">
                    <Input placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="team-name" className="rounded-xl h-11 md:col-span-2" />
                    <Input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="team-email" className="rounded-xl h-11 md:col-span-2" />
                    <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl h-11" />
                    <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="team-role" className="rounded-xl border h-11 px-3 text-sm">
                        {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <Input type="password" placeholder="Temporary password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="team-password" className="rounded-xl h-11 md:col-span-4" />
                    <Button onClick={create} disabled={saving} data-testid="team-create-btn" className="rounded-full h-11 bg-primary hover:bg-primary/90 md:col-span-2">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4" /> Create member</>}
                    </Button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">The team member will log in at /login using this email + password and land in the Team Portal.</p>
            </div>

            {loading ? <Loader2 className="w-6 h-6 animate-spin text-primary mt-6" /> : (
                <div className="mt-6 rounded-2xl bg-card border overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-left">
                            <tr>
                                <th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Since</th><th className="p-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {members.map((m) => (
                                <tr key={m.id} className="border-t border-border">
                                    <td className="p-3 font-semibold">{m.name}</td>
                                    <td className="p-3"><span className="inline-flex items-center gap-1 text-xs"><Mail className="w-3 h-3 text-muted-foreground" />{m.email}</span></td>
                                    <td className="p-3"><span className="text-xs uppercase tracking-widest px-2 py-0.5 rounded-full bg-primary/10 text-primary">{m.role}</span></td>
                                    <td className="p-3 text-xs text-muted-foreground">{new Date(m.created_at).toLocaleDateString()}</td>
                                    <td className="p-3 text-right">
                                        {m.role !== "admin" && m.role !== "super_admin" && (
                                            <button onClick={() => remove(m)} className="text-destructive hover:opacity-70"><Trash2 className="w-4 h-4" /></button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {members.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-sm text-muted-foreground"><User className="w-6 h-6 mx-auto opacity-40 mb-1" />No team members yet — add one above.</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AdminTeam;
